import { useState, useEffect } from 'react';
import api from '../../services/api';
import useCurrentUser from '../../hooks/useCurrentUser';
import Modal from './Modal';

const STATUT_CONFIG = {
    nouveau:  { label: 'Nouveau',  color: '#ef4444', bg: '#fee2e2', icon: 'ti-alert-circle' },
    en_cours: { label: 'En cours', color: '#d97706', bg: '#fef3c7', icon: 'ti-clock' },
    resolu:   { label: 'Résolu',   color: '#16a34a', bg: '#dcfce7', icon: 'ti-check' },
};

export default function BugReportButton({ checkAndUnlock }) {
    const currentUser = useCurrentUser();
    const [showModal, setShowModal] = useState(false);
    const [onglet, setOnglet] = useState('nouveau'); // 'nouveau' | 'mes'
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [mesReports, setMesReports] = useState([]);
    const [loadingReports, setLoadingReports] = useState(false);

    const loadMesReports = async () => {
        setLoadingReports(true);
        try {
            const res = await api.get('/bug_reports?pagination=false&order[createdAt]=desc');
            const tous = res.data.member || [];
            const moi = tous.filter(r => r.auteur === (currentUser?.nom || currentUser?.email));
            setMesReports(moi);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingReports(false);
        }
    };

    useEffect(() => {
        if (showModal && onglet === 'mes') loadMesReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showModal, onglet]);

    const handleSubmit = async () => {
        if (!message.trim()) return;
        setSending(true);
        try {
            await api.post('/bug_reports', {
                auteur: currentUser?.nom || currentUser?.email || 'Inconnu',
                message: message.trim(),
            }, { headers: { 'Content-Type': 'application/ld+json' } });
            checkAndUnlock('signaleur');
            setSent(true);
            setMessage('');
            setTimeout(() => {
                setSent(false);
                setShowModal(false);
            }, 2000);
        } catch (err) {
            console.error(err);
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <button
                onClick={() => { setShowModal(true); setOnglet('nouveau'); }}
                title="Signaler un bug"
                style={{
                    position: 'fixed', bottom: '24px', right: '24px', zIndex: 999,
                    width: '48px', height: '48px', borderRadius: '50%',
                    backgroundColor: '#ef4444', color: 'white', border: 'none',
                    cursor: 'pointer', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', boxShadow: '0 4px 12px rgba(239,68,68,0.4)',
                    fontSize: '20px',
                }}
            >
                <i className="ti ti-bug" />
            </button>

            {showModal && (
                <Modal title="Signaler un bug" onClose={() => setShowModal(false)}>
                    {/* Onglets */}
                    <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: '12px' }}>
                        {[
                            { id: 'nouveau', label: 'Nouveau signalement', icon: 'ti-plus' },
                            { id: 'mes', label: 'Mes signalements', icon: 'ti-list' },
                        ].map(o => (
                            <button key={o.id} onClick={() => setOnglet(o.id)} style={{
                                padding: '6px 14px', borderRadius: '8px', border: 'none',
                                backgroundColor: onglet === o.id ? '#fee2e2' : 'transparent',
                                color: onglet === o.id ? '#ef4444' : '#888',
                                fontWeight: onglet === o.id ? '700' : '400',
                                fontSize: '13px', cursor: 'pointer',
                            }}>
                                <i className={`ti ${o.icon}`} style={{ marginRight: '5px' }} />
                                {o.label}
                            </button>
                        ))}
                    </div>

                    {/* Onglet nouveau signalement */}
                    {onglet === 'nouveau' && (
                        sent ? (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#16a34a' }}>
                                <i className="ti ti-check" style={{ fontSize: '32px', display: 'block', marginBottom: '8px' }} />
                                Message envoyé, merci !
                            </div>
                        ) : (
                            <div>
                                <div style={{ marginBottom: '16px', fontSize: '13px', color: '#888' }}>
                                    <i className="ti ti-user" style={{ marginRight: '6px' }} />
                                    {currentUser?.nom || currentUser?.email}
                                    <span style={{ marginLeft: '12px' }}>
                                        <i className="ti ti-clock" style={{ marginRight: '4px' }} />
                                        {new Date().toLocaleString('fr-FR')}
                                    </span>
                                </div>
                                <textarea
                                    value={message}
                                    onChange={e => setMessage(e.target.value)}
                                    placeholder="Décrivez le problème rencontré..."
                                    rows={5}
                                    style={{
                                        width: '100%', padding: '10px 12px', borderRadius: '8px',
                                        border: '1px solid #ddd', fontSize: '14px', resize: 'vertical',
                                        boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: '16px',
                                    }}
                                />
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                    <button onClick={() => setShowModal(false)} style={{
                                        padding: '8px 16px', borderRadius: '8px', border: '1px solid #ddd',
                                        backgroundColor: 'white', cursor: 'pointer', fontSize: '13px',
                                    }}>Annuler</button>
                                    <button onClick={handleSubmit} disabled={sending || !message.trim()} style={{
                                        padding: '8px 16px', borderRadius: '8px', border: 'none',
                                        backgroundColor: sending ? '#fca5a5' : '#ef4444',
                                        color: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                                    }}>{sending ? 'Envoi...' : 'Envoyer'}</button>
                                </div>
                            </div>
                        )
                    )}

                    {/* Onglet mes signalements */}
                    {onglet === 'mes' && (
                        loadingReports ? (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#888' }}>Chargement...</div>
                        ) : mesReports.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                                <i className="ti ti-check" style={{ fontSize: '24px', display: 'block', marginBottom: '8px' }} />
                                Aucun signalement
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '300px', overflowY: 'auto' }}>
                                {mesReports.map(r => {
                                    const cfg = STATUT_CONFIG[r.statut] || STATUT_CONFIG.nouveau;
                                    return (
                                        <div key={r.id} style={{
                                            borderRadius: '8px', padding: '12px 14px',
                                            backgroundColor: cfg.bg,
                                            borderLeft: `3px solid ${cfg.color}`,
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                                <span style={{ fontSize: '12px', fontWeight: '700', color: cfg.color }}>
                                                    <i className={`ti ${cfg.icon}`} style={{ marginRight: '4px' }} />
                                                    {cfg.label}
                                                </span>
                                                <span style={{ fontSize: '11px', color: '#888' }}>
                                                    {new Date(r.createdAt).toLocaleDateString('fr-FR')}
                                                </span>
                                            </div>
                                            <div style={{ fontSize: '13px', color: '#444', whiteSpace: 'pre-wrap' }}>
                                                {r.message}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )
                    )}
                </Modal>
            )}
        </>
    );
}